import {
    ActionButton,
    Checkbox,
    DetailsHeader,
    DetailsListLayoutMode,
    Icon,
    IconButton,
    IDetailsHeaderProps,
    IRenderFunction,
    Label,
    PrimaryButton,
    SearchBox,
    SelectionMode,
    Stack,
    StackItem,
} from "@fluentui/react";
import React, {ReactElement, useCallback, useEffect, useMemo, useRef, useState,} from "react";
import {TFunction, useTranslation} from "react-i18next";
import hash from "object-hash";
import _ from "lodash";
import {usePrevious} from "@fluentui/react-hooks";
import {useTenantResourceList} from "../hooks/useTenantResourceList";
import {ITenantResourceQuery, ITenantResourceQueryCase,} from "../interfaces/ITenantResourceQuery";
import "../styles/TenantItemList.css";
import {RenderElementForKeyExported, TenantForm} from "./TenantForm";
import {TenantPagination} from "./TenantPagination";
import {GetApiError} from "./ShowApiError";
import {OnixGroupedList} from "./OnixGroupedList";
import {OnixCallout} from "./OnixCallout";
import {FormAction,} from "../types/TenantTypes";
import {ITenantFormLayout, ITenantList,} from "./OnixElements";
import {DropdownFromList} from "./DropdownFromList";
import {theme} from "..";
import {UUIDHelper} from "../helpers/UUIDHelper";

export const TenantList = <TDatabaseModel, TDto, TReturn>({
                                                              children,
                                                              renderRow,
                                                              horizontal,
                                                              compactPaging,
                                                              storageMapping,
                                                              preserveSelection,
                                                              getId,
                                                              formChildren,
                                                              className,
                                                              stretchForm,
                                                              query,
                                                              id,
                                                              allowedActions,
                                                              additionalCommands,
                                                              footer,
                                                              resource,
                                                              addingTitle,
                                                              editingTitle,
                                                              form,
                                                              baseDto,
                                                              onAddDialogOpen,
                                                              columns,
                                                              itemInvoked,
                                                              queryIsRequired,
                                                              allowSearching,
                                                              pad,
                                                              additionalSelectionCommands,
                                                              orderBy: orderByInitial,
                                                              orderDescending: orderDescendingInitial,
                                                              allowedGroupKeys,
                                                              reloadRef,
                                                              allowShowAll,
                                                              sidebar,
                                                              style,
                                                              keepFilterColumns,
                                                              defaultGroups,
                                                              hideTopBar,
                                                              editItemRef,
                                                              deleteItemRef,
                                                              resultIsEmpty,
                                                              resultsChanged,
                                                              selectionModeOverride,
                                                              additionalIntParameter,
                                                              reloadSingleRef,
                                                              hideNoItemsFooter,
                                                              afterFormAction,
                                                              selectionChanged,
                                                              selectionItemsChanged,
                                                              hideGrouping,
                                                              title,
                                                              dontUseStickyTopBar,
                                                              listStyle,
                                                              wrapStyle,
                                                              hideGroupTitle,
                                                              makeGroupHeaderAddonText,
                                                              additionalStringParameter,
                                                              debug,
                                                              hideIfEmpty,
                                                              disablePaging,
                                                              disableWrappingOnChildren,
                                                              afterInsertFocusInserted,
                                                              quickFilters,
                                                              minFormWidth,
                                                              disabled: listIsDisabled,
                                                              minimalTopBar,
                                                              ignoreTenancy,
                                                              beforeSubmitForm,
                                                              headers,
                                                              clearSelectionRef,
                                                              dontSubmitOnEnter,
                                                              hideSaveAndCloseOnForm,
                                                          }: ITenantList<TDatabaseModel, TDto, TReturn>) => {
    const tableIdentifier = useMemo<string>(
        () =>
            hash({
                columns: columns.map((m) => m.fieldName),
                allowedGroupKeys,
                form,
                addingTitle,
                editingTitle,
                id,
                baseDto,
                resource,
                allowedActions,
            }),
        [
            columns,
            allowedGroupKeys,
            allowedActions,
            form,
            addingTitle,
            editingTitle,
            id,
            baseDto,
            resource,
        ],
    );

    const {
        setQuery,
        result,
        setSelectedItem,
        selectedItem,
        removeSelected,
        setSearch,
        search,
        paging,
        reload,
        previousPage,
        nextPage,
        setOrderDescending,
        orderDescending,
        orderBy,
        setOrderBy,
        showAll,
        loading,
        reloadSingle,
        duplicate,
        query: queryLocal,
    } = useTenantResourceList<TDatabaseModel, TDto, TReturn>(
        resource,
        tableIdentifier,
        undefined,
        queryIsRequired,
        !disablePaging,
        orderByInitial,
        orderDescendingInitial,
        getId,
        additionalIntParameter,
        additionalStringParameter,
        afterFormAction,
        undefined,
        undefined,
        undefined,
        undefined,
        ignoreTenancy,
        beforeSubmitForm,
        headers,
    );
    const {t} = useTranslation();
    const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);
    const timeout = useRef<number>();
    const [searchString, setSearchString] = useState(search);

    useEffect(() => {
        if (addDialogOpen && !form) {
            console.error("Form (form) is required to show add dialog!");
        }
    }, [addDialogOpen, form]);

    useEffect(() => {
        if (selectionChanged) {
            selectionChanged(selectedItem);
        }
    }, [selectedItem, selectionChanged]);

    useEffect(() => {
        if (editItemRef) {
            editItemRef.current = (item) => {
                itemInvokedLocal(item);
            };
        }
    }, [editItemRef]);

    useEffect(() => {
        if (result) {
            if (result.items) {
                if (!result.items.length && resultIsEmpty) resultIsEmpty();
                if (resultsChanged) resultsChanged(result.items);
            }
        }
    }, [result, resultIsEmpty, resultsChanged]);

    useEffect(() => {
        if (deleteItemRef) {
            deleteItemRef.current = (item) =>
                new Promise((resolve, reject) => {
                    removeSelected(item)
                        .then(() => {
                            resolve();
                            reload();
                        })
                        .catch(reject)
                        .finally(resolve);
                });
        }
    }, [deleteItemRef, setSelectedItem, removeSelected, reload]);

    useEffect(() => {
        if (reloadRef) {
            reloadRef.current = reload;
        }
    }, [reload, reloadRef]);

    useEffect(() => {
        if (reloadSingleRef) {
            reloadSingleRef.current = async (item) => {
                const ret = await reloadSingle(item);
                // setTimeout(() => {
                //     if (preserveSelection) {
                //         setSelectedItem(item);
                //     }
                // }, 100);
                return ret;
            };
        }
    }, [reload, reloadSingleRef]);

    const prev = usePrevious(query);

    useEffect(() => {
        if (query) {
            if (!_.isEqual(query, prev)) {
                setQuery(query);
            }
        } else {
            reload();
        }
    }, [query, prev]);

    const dismiss = useCallback(() => {
        setSelectedItem(undefined);
        setAddDialogOpen(false);
    }, []);

    const openAddDialog = useCallback(async () => {
        if (!form) {
            alert("Form is required to add/edit item!");
        }

        try {
            if (onAddDialogOpen) {
                await onAddDialogOpen();
            }
        } catch (err) {
            console.error("Error while preparing add dialog", err);
        }

        dismiss();
        setAddDialogOpen(true);
    }, [dismiss, form, onAddDialogOpen]);

    const removeSelectedClicked = useCallback(() => {
        removeSelected()
            .then(dismiss)
            .catch(({response: {data}}) =>
                alert(t("there_was_an_error") + ": " + GetApiError(data)),
            );
    }, [removeSelected, dismiss, t]);

    const editSelectedClicked = useCallback(() => {
        if (!form) {
            alert("Form is required to add/edit item!");
        }
        setAddDialogOpen(true);
    }, []);

    const itemInvokedLocal = useCallback(
        (item: TReturn) => {
            if (
                !allowedActions ||
                allowedActions.indexOf("PUT") != -1 ||
                allowedActions.indexOf("POST") != -1
            ) {
                if (!form) {
                    alert("Form is required to add/edit item!");
                }
                if (itemInvoked) itemInvoked(item);
                else {
                    setSelectedItem(item);
                    setAddDialogOpen(true);
                }
            } else {
                if (itemInvoked) itemInvoked(item);
                else {
                    setSelectedItem(item);
                }
            }
        },
        [allowedActions, form, itemInvoked, setSelectedItem],
    );

    useEffect(() => {
        if (timeout.current) clearTimeout(timeout.current);
        timeout.current = window.setTimeout(() => {
            setSearch(searchString);
        }, 300);
    }, [searchString, timeout]);

    const searchChanged = useCallback((v: string) => {
        setSearchString(v);
    }, []);

    const checkAndRemoveSelected = useCallback(() => {
        if (selectedItem) {
            removeSelectedClicked();
            // let columnStrings = columns.slice(0, 2).map(m => (selectedItem as any)[m.fieldName]);

            // areYouSure(
            //     removeSelectedClicked,
            //     "are-you-sure-you-want-to-delete",
            //     message => message.replace("{0}", columnStrings.join(', ')))
        }
    }, [removeSelectedClicked, selectedItem, columns]);

    const nearItems = useCallback(
        (onDismiss?: () => void, orientation?: "VERTICAL" | "HORIZONTAL") => {
            let items: JSX.Element[] = [];
            const isVertical = orientation == "VERTICAL";
            const isHorizontal = orientation == "HORIZONTAL";
            if (!allowedActions || allowedActions.indexOf("POST") != -1) {
                items.push(
                    <ActionButton
                        {...{
                            key: "add",
                            iconProps: {
                                iconName: "Add",
                            },
                            onClick: openAddDialog,
                            text: isHorizontal && minimalTopBar ? undefined : t("add"),
                            primaryDisabled: false,
                            disabled: loading,
                            height: 32,
                            style: {
                                height: 32,
                            },
                        }}
                        disabled={listIsDisabled}
                    />,
                );
            }

            if (selectedItem) {
                if (!allowedActions || allowedActions.indexOf("POST") != -1) {
                    items.push(
                        <ActionButton
                            {...{
                                key: "duplicate",
                                iconProps: {
                                    iconName: "Copy",
                                },
                                onClick: () => {
                                    duplicate({
                                        ...selectedItem,
                                        uniqueClientId: UUIDHelper(),
                                    });
                                },
                                text:
                                    isHorizontal && minimalTopBar ? undefined : t("duplicate"),
                                disabled: loading,
                                height: 32,
                                style: {
                                    height: 32,
                                },
                            }}
                            disabled={listIsDisabled}
                        />,
                    );
                }
                if (!allowedActions || allowedActions.indexOf("PUT") != -1) {
                    items.push(
                        <ActionButton
                            {...{
                                key: "edit",
                                iconProps: {
                                    iconName: "Edit",
                                },
                                onClick: editSelectedClicked,
                                text: isHorizontal && minimalTopBar ? undefined : t("edit"),
                                disabled: loading,
                                height: 32,
                                style: {
                                    height: 32,
                                },
                            }}
                            disabled={listIsDisabled}
                        />,
                    );
                }
                if (!allowedActions || allowedActions.indexOf("DELETE") != -1) {
                    items.push(
                        <OnixCallout
                            key="delete"
                            disabled={listIsDisabled}
                            keyData={"delete-item-callout"}
                            areYouSure={{
                                title: "want-to-delete-item",
                                onYes: () => {
                                    checkAndRemoveSelected();
                                    if (onDismiss) onDismiss();
                                },
                            }}
                            trigger={(id, toggle) => (
                                <ActionButton
                                    id={id}
                                    {...{
                                        key: "delete",
                                        iconProps: {
                                            iconName: "Trash",
                                        },
                                        onClick: (e) => toggle(e, undefined),
                                        text:
                                            isHorizontal && minimalTopBar ? undefined : t("delete"),
                                        disabled: loading,
                                        height: 32,
                                        style: {
                                            height: 32,
                                        },
                                    }}
                                    disabled={listIsDisabled}
                                />
                            )}
                        />,
                    );
                }
                if (additionalSelectionCommands) {
                    const additionals = additionalSelectionCommands(
                        selectedItem as TReturn,
                    ) as JSX.Element[];
                    additionals.forEach((m, i) => {
                        console.assert(
                            !!m.key,
                            `Near items additional selection ${i}th command has no key: ${m.key} in table ${id} ${resource}`,
                        );
                    });
                    items = items.concat(additionals);
                }
            }
            if (additionalCommands) {
                additionalCommands.forEach((m, i) => {
                    console.assert(
                        !!m.key,
                        `Near items additional ${i}th command has no key: ${m.key} in table ${id} ${resource}`,
                    );
                });
                items = items.concat(additionalCommands);
            }

            return items;
        },
        [
            allowedActions,
            openAddDialog,
            additionalSelectionCommands,
            selectedItem,
            removeSelectedClicked,
            editSelectedClicked,
            t,
            additionalCommands,
            loading,
            id,
            resource,
            listIsDisabled,
            minimalTopBar,
        ],
    );

    const farItems: JSX.Element[] = useMemo(() => {
        const items: JSX.Element[] = [];
        if (allowSearching) {
            items.push(
                <SearchBox
                    disabled={listIsDisabled}
                    value={searchString}
                    styles={{root: {marginLeft: "0 !important"}}}
                    onChange={(event, newValue) => searchChanged(newValue ?? "")}
                    placeholder={t("search")}
                    key="search"
                />,
            );

            if (form || quickFilters) {
                items.push(
                    <OnixCallout
                        key="filter"
                        disabled={listIsDisabled}
                        titleless
                        heightless
                        trigger={(id, toggle) => (
                            <ActionButton
                                disabled={listIsDisabled}
                                style={
                                    !!queryLocal && !!Object.keys(queryLocal).length
                                        ? {
                                            backgroundColor: theme.palette.themePrimary,
                                            color: "white",
                                        }
                                        : {}
                                }
                                primary={!!queryLocal && !!Object.keys(queryLocal).length}
                                iconProps={{
                                    iconName: "Filter",
                                    style: {
                                        color:
                                            !!queryLocal && !!Object.keys(queryLocal).length
                                                ? "white"
                                                : undefined,
                                    },
                                }}
                                id={id}
                                onClick={(e) => toggle(e, undefined)}
                            >
                                Filter
                            </ActionButton>
                        )}
                    >
                        {(realign, show) => (
                            <QuickFiltersElement<TReturn, TDatabaseModel>
                                quickFilters={(form ?? quickFilters)!}
                                t={t}
                                disabled={listIsDisabled}
                                key={"quick-filters"}
                                query={queryLocal}
                                setQuery={setQuery}
                            />
                        )}
                    </OnixCallout>,
                );
            }
        }
        if (!hideTopBar && !disablePaging) {
            items.push(
                <TenantPagination
                    disabled={listIsDisabled}
                    paginate={true}
                    nextPage={nextPage}
                    paging={paging}
                    compact
                    showAll={allowShowAll ? showAll : undefined}
                    previousPage={previousPage}
                    result={result}
                    key="pagination"
                />,
            );
        }
        return items;
    }, [
        searchChanged,
        nextPage,
        disablePaging,
        paging,
        previousPage,
        result,
        footer,
        t,
        search,
        searchString,
        compactPaging,
        allowShowAll,
        hideTopBar,
        quickFilters,
        queryLocal,
        setQuery,
        form,
        listIsDisabled,
        minimalTopBar,
    ]);

    const filterBy = useCallback(
        (column: any) => {
            if (orderBy == column) {
                setOrderDescending(!orderDescending);
            } else {
                setOrderBy(column);
            }
        },
        [orderBy, orderDescending, setOrderBy, setOrderDescending],
    );

    const renderToolTip: IRenderFunction<any> = useCallback(
        (props, defaultRenderer) => {
            const id = props?.id?.split("-")[1];
            const isFilteredBy = orderBy === id;
            return (
                <Stack
                    horizontal
                    styles={{
                        root: {color: isFilteredBy ? "black" : "#888", padding: 0},
                        inner: {padding: 0},
                    }}
                    onClick={() => filterBy(id)}
                >
                    {props?.children}
                    {isFilteredBy ? (
                        <Icon
                            styles={{
                                root: {
                                    paddingRight: 10,
                                    paddingLeft: 10,
                                    fontWeight: "bolder",
                                },
                            }}
                            iconName={!orderDescending ? "ChevronUp" : "ChevronDown"}
                        />
                    ) : null}
                </Stack>
            );
        },
        [orderBy, orderDescending, setOrderBy, setOrderDescending, filterBy],
    );

    // useKey("Backspace", checkAndRemoveSelected, {}, [checkAndRemoveSelected]);
    // useKey("Delete", checkAndRemoveSelected, {}, [checkAndRemoveSelected]);

    const renderHeader: IRenderFunction<IDetailsHeaderProps> = (
        props,
        defaultRenderer,
    ) => {
        if (hideTopBar) return null;
        return (
            <DetailsHeader
                {...props}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                onRenderColumnHeaderTooltip={renderToolTip}
            />
        );
    };

    useEffect(() => {
        if (debug) {
            // console.log("Selected item", tableIdentifier, selectedItem, result?.items);
        }
    }, [debug, tableIdentifier, selectedItem, result]);

    const childItems = useMemo(
        () =>
            horizontal ? (
                <StackItem
                    styles={{root: {flex: 1, maxWidth: "50%", overflow: "scroll"}}}
                >
                    {children ? children(reload, selectedItem as any) : null}
                </StackItem>
            ) : children ? (
                children(reload, selectedItem as any)
            ) : null,
        [children, reload, selectedItem],
    );

    // nije dobro, treba već kod tenant
    const localAfterFormAction = useCallback(
        (formAction: FormAction, dto: TReturn) => {
            if (afterFormAction) {
                afterFormAction(formAction, dto);
            }
            if (afterInsertFocusInserted && formAction === "POST") {
                // setSearchString((getId ?? ((d) => (d as any)["id"]))(dto));
            }
        },
        [afterFormAction, getId, setSearchString, afterInsertFocusInserted],
    );

    const makeRightSideAddon = useCallback(
        () => (
            <>
                {farItems.map((m, i) => {
                    console.assert(
                        !!m.key,
                        `Right side addon > far items ${i}th item has no key: ${m.key}`,
                    );
                    return m;
                })}
            </>
        ),
        [farItems],
    );

    if (hideIfEmpty && result?.items?.length == 0) return null;

    return (
        <Stack
            horizontal={horizontal}
            horizontalAlign="stretch"
            className="tenant-list-wrapper-stack"
        >
            <StackItem
                styles={{
                    root: {
                        flex: 1,
                        maxWidth: horizontal ? "50%" : undefined,
                        overflow: "scroll",
                    },
                }}
                id={id}
                style={wrapStyle}
            >
                <div className="tenantItemListClass">
                    <OnixGroupedList<TReturn>
                        allowedGroupKeys={allowedGroupKeys ?? []}
                        columns={columns as any}
                        disabled={listIsDisabled}
                        items={result?.items as any}
                        onItemInvoked={itemInvokedLocal}
                        className={className}
                        onRenderRow={renderRow}
                        leftSideAddon={(_, __, orientation, onDismiss) => (
                            <Stack horizontal={orientation == "HORIZONTAL"}>
                                {nearItems(onDismiss, orientation).map((m, i) => {
                                    console.assert(
                                        !!m.key,
                                        `Left side addon > near items ${i}th item has no key: ${m.key}`,
                                    );
                                    if (orientation == "VERTICAL") {
                                        return (
                                            <div
                                                key={m.key}
                                                style={{minWidth: "100%"}}
                                                className={"vertical-action-tenant-item"}
                                            >
                                                {m}
                                            </div>
                                        );
                                    }
                                    return m;
                                })}
                            </Stack>
                        )}
                        style={style}
                        listStyle={listStyle}
                        title={title}
                        makeGroupHeaderAddonText={makeGroupHeaderAddonText}
                        dontUseStickyTopBar={dontUseStickyTopBar}
                        hideGrouping={hideGrouping}
                        hideTopBar={hideTopBar}
                        getId={getId}
                        clearSelectionRef={clearSelectionRef}
                        hideGroupTitle={hideGroupTitle}
                        keepFilterColumns={keepFilterColumns}
                        rightSideAddon={makeRightSideAddon}
                        selectionPreservedOnEmptyClick={preserveSelection}
                        selectionMode={
                            hideTopBar
                                ? SelectionMode.none
                                : (selectionModeOverride ?? SelectionMode.single)
                        }
                        itemSelected={setSelectedItem}
                        itemsSelected={selectionItemsChanged}
                        tableIdentifier={tableIdentifier}
                        defaultGroups={defaultGroups}
                        onRenderDetailsHeader={renderHeader}
                        sidebar={sidebar}
                        loading={loading}
                        hideNoItemsFooter={hideNoItemsFooter}
                    >
                        {(clearSelection) => (
                            <>
                                {addDialogOpen && form ? (
                                    <TenantForm
                                        dontSubmitOnEnter={dontSubmitOnEnter}
                                        formDisabled={listIsDisabled}
                                        stretch={stretchForm}
                                        additionalIntParameter={additionalIntParameter}
                                        additionalStringParameter={additionalStringParameter}
                                        baseModel={selectedItem}
                                        dismiss={() => {
                                            clearSelection();
                                            dismiss();
                                        }}
                                        beforeSubmitForm={beforeSubmitForm}
                                        minFormWidth={minFormWidth}
                                        reload={reload}
                                        hideSaveAndCloseOnForm={hideSaveAndCloseOnForm}
                                        afterFormAction={localAfterFormAction}
                                        addingTitle={addingTitle}
                                        getId={getId}
                                        storageMapping={storageMapping}
                                        disableWrappingOnChildren={disableWrappingOnChildren}
                                        editingTitle={editingTitle}
                                        baseDto={baseDto}
                                        form={form}
                                        resource={resource}
                                    >
                                        {formChildren}
                                    </TenantForm>
                                ) : null}
                            </>
                        )}
                    </OnixGroupedList>
                </div>
                {footer ? <Stack>{footer(result?.items)}</Stack> : null}
            </StackItem>
            {childItems}
        </Stack>
    );
};

interface IQuickFiltersElement<TReturn> {
    quickFilters: ITenantFormLayout<TReturn>;
    t: TFunction;
    query: ITenantResourceQuery<TReturn> | undefined;
    setQuery: (query: ITenantResourceQuery<TReturn>) => void;
    disabled?: boolean;
}

function QuickFiltersElement<TReturn, TDatabaseModel>({
                                                          quickFilters,
                                                          t,
                                                          query,
                                                          setQuery,
                                                          disabled,
                                                      }: IQuickFiltersElement<TReturn>): ReactElement {
    console.log("QuickFiltersElement", quickFilters, query);
    const [resourceQuery, setResourceQuery] = useState<
        ITenantResourceQuery<TReturn>
    >(query ?? ({} as any));
    const [storage, setStorage] = useState<{ [key: string]: any }>({});

    // const [activeResourceQuerykey, setActiveResourceQueryKey] = useState<{
    //   [key in keyof TReturn]: keyof ITenantResourceQuery<TReturn>;
    // }>({} as any);

    // const [dto, setDto] = useState<TReturn & Partial<TDatabaseModel> & TWithStorages<TReturn & Partial<TDatabaseModel>>>({} as any);

    // const fieldChanged: (formKey: keyof TReturn, fieldValue: any) => Promise<void> = useCallback((formKey: keyof TReturn, fieldChanges: any) => {
    //   setDto((prev) => ({ ...prev, ...fieldChanges }));
    //   return Promise.resolve();
    // }, [dto]);

    const typeTypes: (keyof ITenantResourceQueryCase<TReturn>)[] = useMemo(
        () => [
            "Equals",
            "GreaterThan",
            "GreaterThanOrEqual",
            "LessThan",
            "LessThanOrEqual",
        ],
        [],
    );
    const boolTypes: (keyof ITenantResourceQueryCase<TReturn>)[] = useMemo(
        () => ["Truthy", "IsNull"],
        [],
    );
    const arrayTypes: (keyof ITenantResourceQueryCase<TReturn>)[] = useMemo(
        () => ["AnyOf"],
        [],
    );

    const changeResourceQueryKeyType = useCallback(
        (key: keyof TReturn, type?: keyof ITenantResourceQueryCase<TReturn>) => {
            if (type) {
                const current = resourceQuery[key];
                const currentActiveKeyType = Object.keys(
                    current ?? {},
                )[0] as keyof ITenantResourceQueryCase<TReturn>;
                const currentIsTypeType = typeTypes.includes(currentActiveKeyType);
                const currentIsBoolType = boolTypes.includes(currentActiveKeyType);
                const currentIsArrayType = arrayTypes.includes(currentActiveKeyType);

                const newIsTypeType = typeTypes.includes(type);
                const newIsBoolType = boolTypes.includes(type);
                const newIsArrayType = arrayTypes.includes(type);

                if (
                    (currentIsTypeType && newIsTypeType) ||
                    (currentIsBoolType && newIsBoolType) ||
                    (currentIsArrayType && newIsArrayType)
                ) {
                    if (current) {
                        setResourceQuery((prev) => ({
                            ...prev,
                            [key]: {
                                [type]: current[currentActiveKeyType],
                            },
                        }));
                    } else {
                        setResourceQuery((prev) => ({
                            ...prev,
                            [key]: {
                                [type]: undefined,
                            },
                        }));
                    }
                } else {
                    setResourceQuery((prev) => ({
                        ...prev,
                        [key]: {
                            [type]: undefined,
                        },
                    }));
                }
            } else {
                setResourceQuery((prev) => {
                    const newQuery = {...prev};
                    delete newQuery[key];
                    return newQuery;
                });
            }
        },
        [resourceQuery],
    );

    const fieldChanged = useCallback(
        (
            formKey: keyof TReturn,
            fieldValue: any,
            atIndex?: number,
        ): Promise<void> => {
            const current = resourceQuery[formKey];
            const currentActiveKeyType = Object.keys(
                current ?? {},
            )[0] as keyof ITenantResourceQueryCase<TReturn>;

            if (current && currentActiveKeyType) {
                const newValue = fieldValue?.[formKey];
                if (atIndex != undefined) {
                    const currentArray = current[currentActiveKeyType] as string[];
                    const newArray = [...currentArray];
                    newArray[atIndex] = newValue;
                    setResourceQuery((prev) => ({
                        ...prev,
                        [formKey]: {
                            [currentActiveKeyType]: newArray,
                        },
                    }));
                    setStorage((prev) => ({
                        ...prev,
                        [`${formKey.toString()}-${atIndex}`]:
                            fieldValue[`${formKey.toString()}storage`],
                    }));
                } else if (newValue != undefined) {
                    setResourceQuery((prev) => ({
                        ...prev,
                        [formKey]: {
                            [currentActiveKeyType]: newValue,
                        },
                    }));
                    setStorage((prev) => ({
                        ...prev,
                        [formKey.toString()]: fieldValue[`${formKey.toString()}storage`],
                    }));
                } else {
                    setResourceQuery((prev) => ({
                        ...prev,
                        [formKey]: {
                            [currentActiveKeyType]: "",
                        },
                    }));
                    setStorage((prev) => ({
                        ...prev,
                        [formKey.toString()]: undefined,
                    }));
                }
            }

            return Promise.resolve();
        },
        [resourceQuery, setResourceQuery],
    );

    const submit = useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e?.preventDefault();

            setQuery(resourceQuery);
        },
        [resourceQuery],
    );

    const addNewEmptyValueToAnyOf = useCallback(
        (key: keyof TReturn | string) => {
            const current = resourceQuery[key as keyof TReturn];
            const currentActiveKeyType = Object.keys(
                current ?? {},
            )[0] as keyof ITenantResourceQueryCase<TReturn>;
            if (current && currentActiveKeyType) {
                setResourceQuery((prev) => ({
                    ...prev,
                    [key]: {
                        [currentActiveKeyType]: [
                            ...((current[currentActiveKeyType] ?? []) as string[]),
                            "",
                        ],
                    },
                }));
            }
        },
        [resourceQuery, setResourceQuery],
    );

    const removeItemFromAnyOf = useCallback(
        (key: keyof TReturn | string, index: number) => {
            const current = resourceQuery[key as keyof TReturn];
            const currentActiveKeyType = Object.keys(
                current ?? {},
            )[0] as keyof ITenantResourceQueryCase<TReturn>;
            if (current && currentActiveKeyType) {
                const newArray = [
                    ...((current[currentActiveKeyType] ?? []) as string[]),
                ];
                newArray.splice(index, 1);
                setResourceQuery((prev) => ({
                    ...prev,
                    [key]: {
                        [currentActiveKeyType]: newArray,
                    },
                }));
            }
        },
        [resourceQuery, setResourceQuery],
    );

    const getOperatorIcon = useCallback((key: string): string => {
        return {
            Equals: "CalculatorEqualTo",
            GreaterThan: "ChevronUp",
            GreaterThanOrEqual: "ChevronUpEnd6",
            LessThan: "ChevronDown",
            LessThanOrEqual: "ChevronDownEnd6",
            Truthy: "CheckboxFill",
            IsNull: "Checkbox",
            AnyOf: "BulletedTreeList",
        }[key]!;
    }, []);

    const renderOperatorIcon = useCallback((value: string): ReactElement => {
        const operatorIcon = getOperatorIcon(value);

        return <Icon iconName={operatorIcon}/>;
    }, []);

    console.log("storages", storage);

    return (
        <Stack style={{width: 750}}>
            <form onSubmit={submit}>
                <table>
                    <tbody>
                    {Object.keys(quickFilters).map((m, i) => {
                        const activeResourceQuerykey =
                            resourceQuery[m as keyof TReturn] != undefined
                                ? Object.keys((resourceQuery as any)[m as any] as any)?.[0]
                                : undefined;
                        const activeResourceQueryValue = activeResourceQuerykey
                            ? ((resourceQuery as any)[m as any] as any)[
                                activeResourceQuerykey as keyof ITenantResourceQueryCase<TReturn>
                                ]
                            : undefined;

                        return (
                            <tr>
                                <td style={{verticalAlign: "top", width: 200}}>
                                    <Stack
                                        style={{width: 200}}
                                        horizontal
                                        horizontalAlign="stretch"
                                        grow
                                        verticalAlign="start"
                                        verticalFill
                                    >
                                        <Label>{t(m.toString())}</Label>
                                        {/* &nbsp;
                  <Label>{t("is-nominative")}</Label> */}
                                    </Stack>
                                </td>
                                <td style={{verticalAlign: "top", width: 200}}>
                                    <div style={{width: "100%"}}>
                                        <DropdownFromList<keyof ITenantResourceQueryCase<TReturn>>
                                            list={[...typeTypes, ...boolTypes, ...arrayTypes]}
                                            toKND={(m) => ({
                                                data: m,
                                                key: m,
                                                name: t(`QueryCase-${m.toString()}`),
                                            })}
                                            value={activeResourceQuerykey}
                                            setValue={(caseType) => {
                                                changeResourceQueryKeyType(
                                                    m as keyof TReturn,
                                                    caseType,
                                                );
                                            }}
                                            showEmpty={true}
                                            rightSideIconProps={{
                                                iconProps: {
                                                    iconName: getOperatorIcon(
                                                        activeResourceQuerykey as string,
                                                    ),
                                                },
                                            }}
                                            iconFromValue={(value) => renderOperatorIcon(value)}
                                        />
                                    </div>
                                </td>
                                <td style={{verticalAlign: "top", width: 300}}>
                                    <StackItem grow>
                                        {activeResourceQuerykey &&
                                            (typeTypes.includes(
                                                activeResourceQuerykey as keyof ITenantResourceQueryCase<TReturn>,
                                            ) ? (
                                                <RenderElementForKeyExported<TReturn, TDatabaseModel>
                                                    k={m}
                                                    dto={
                                                        {
                                                            [m]: (activeResourceQueryValue as any) ?? "",
                                                            [`${m}storage`]: storage[m] ?? "",
                                                        } as any
                                                    }
                                                    dtoErrors={undefined}
                                                    fieldChanged={fieldChanged}
                                                    form={quickFilters}
                                                    formDisabled={false}
                                                    getId={undefined}
                                                    loading={!activeResourceQuerykey}
                                                    loadingLocal={!activeResourceQuerykey}
                                                    setDto={(dto: any) => {
                                                    }}
                                                    setLoadingFiles={() => {
                                                    }}
                                                    setNotLoadingFiles={() => {
                                                    }}
                                                    t={t}
                                                    uniqueClientId="quickFilters"
                                                    alignment={undefined}
                                                    withoutLabels={true}
                                                />
                                            ) : arrayTypes.includes(
                                                activeResourceQuerykey as keyof ITenantResourceQueryCase<TReturn>,
                                            ) ? (
                                                <Stack
                                                    horizontalAlign="stretch"
                                                    style={{width: "100%"}}
                                                >
                                                    {((activeResourceQueryValue ?? []) as string[]).map(
                                                        (v, i) => {
                                                            const listItemValue = (
                                                                activeResourceQueryValue as string[]
                                                            )?.[i];

                                                            return (
                                                                <Stack style={{marginBottom: 5}}>
                                                                    <Stack key={`${m}-${i}`} horizontal>
                                                                        <StackItem grow>
                                                                            {RenderElementForKeyExported<
                                                                                TReturn,
                                                                                TDatabaseModel
                                                                            >({
                                                                                k: m,
                                                                                dto: {
                                                                                    [m]: (listItemValue as any) ?? "",
                                                                                    [`${m}storage`]:
                                                                                    storage[`${m}-${i}`] ?? "",
                                                                                } as any,
                                                                                dtoErrors: undefined,
                                                                                fieldChanged: (field, fieldChanges) =>
                                                                                    fieldChanged(
                                                                                        field,
                                                                                        fieldChanges,
                                                                                        i,
                                                                                    ),
                                                                                form: quickFilters,
                                                                                formDisabled: false,
                                                                                getId: undefined,
                                                                                loading: !activeResourceQuerykey,
                                                                                loadingLocal: !activeResourceQuerykey,
                                                                                setDto: (dto) => {
                                                                                },
                                                                                setLoadingFiles: () => {
                                                                                },
                                                                                setNotLoadingFiles: () => {
                                                                                },
                                                                                t,
                                                                                uniqueClientId: "quickFilters",
                                                                                alignment: undefined,
                                                                                withoutLabels: true,
                                                                            })}
                                                                        </StackItem>
                                                                        <IconButton
                                                                            iconProps={{iconName: "Delete"}}
                                                                            onClick={() =>
                                                                                removeItemFromAnyOf(m, i)
                                                                            }
                                                                        />
                                                                    </Stack>
                                                                </Stack>
                                                            );
                                                        },
                                                    )}
                                                    <PrimaryButton
                                                        onClick={() => addNewEmptyValueToAnyOf(m)}
                                                    >
                                                        {t("add")}
                                                    </PrimaryButton>
                                                </Stack>
                                            ) : boolTypes.includes(
                                                activeResourceQuerykey as keyof ITenantResourceQueryCase<TReturn>,
                                            ) ? (
                                                <Checkbox
                                                    checked={!!activeResourceQueryValue}
                                                    onChange={(e, checked) => {
                                                        fieldChanged(m as keyof TReturn, {
                                                            [m]: checked ? true : false,
                                                        });
                                                    }}
                                                    label={t(!!activeResourceQueryValue ? "yes" : "no")}
                                                />
                                            ) : null)}
                                    </StackItem>
                                </td>
                                <td style={{verticalAlign: "top", width: 50}}>
                                    {!!activeResourceQuerykey && (
                                        <IconButton
                                            iconProps={{iconName: "Delete"}}
                                            onClick={() => {
                                                setResourceQuery((prev) => {
                                                    const newPrev = {...prev};
                                                    delete newPrev[m as keyof TReturn];
                                                    return newPrev;
                                                });
                                            }}
                                        />
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </form>
            <PrimaryButton
                style={{marginTop: 5}}
                onClick={() => setQuery(resourceQuery)}
            >
                {t("search")}
            </PrimaryButton>
        </Stack>
    );
}
